import React, { useState } from 'react';
import { DownloadIcon, LoaderCircleIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { toMarkdown, toPlainText } from '../../utils/notes';
import { runExport } from '../../utils/notesExport';
import type { ExportFormat } from '../../utils/notesExport';
import type { Note } from '../../types/notes';
const FORMATS: {
  key: ExportFormat;
  label: string;
  preserves: string;
}[] = [{
  key: 'pdf',
  label: 'PDF',
  preserves: 'Full formatting, images and tables'
}, {
  key: 'docx',
  label: 'DOCX',
  preserves: 'Headings, lists, tables; images embedded'
}, {
  key: 'txt',
  label: 'Plain text',
  preserves: 'Text and list markers only'
}, {
  key: 'md',
  label: 'Markdown',
  preserves: 'Headings, lists, checkboxes, tables'
}, {
  key: 'image',
  label: 'Image',
  preserves: 'A rendered picture of the note'
}, {
  key: 'cznote',
  label: 'Campuzon Note',
  preserves: 'Everything, including metadata and links'
}];
export function ExportDialog({
  note,
  open,
  onClose
}: {
  note: Note;
  open: boolean;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = FORMATS.find(item => item.key === format)!;
  const textPreview = format === 'md' ? toMarkdown(note) : format === 'txt' ? toPlainText(note) : null;
  const embeddable = note.attachments.filter(item => (item.kind === 'image' || item.kind === 'drawing') && item.previewUrl?.startsWith('data:image')).length;
  async function download() {
    setBusy(true);
    setError(null);
    try {
      await runExport(note, format);
      onClose();
    } catch {
      setError('That export could not be generated. Try another format.');
    } finally {
      setBusy(false);
    }
  }
  return <Modal open={open} onClose={onClose} title="Export this note" description="Your writing belongs to you. Take it out of Campuzon in whichever format you need." width="lg" footer={<>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-id="element-3457" data-mp="u55xw3fy4oyq">
            Cancel
          </Button>
          <Button variant="primary" icon={busy ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-3459" data-mp="kge6bwess6oc" /> : <DownloadIcon className="h-4 w-4" data-id="element-3460" data-mp="geke8cio4azs" />} disabled={busy} onClick={download} data-id="element-3458" data-mp="pdnvrc26sic6">
            {busy ? 'Generating…' : `Download ${selected.label}`}
          </Button>
        </>} data-id="element-3456" data-mp="2oo0hwxl1thi4">
      <div className="space-y-4" data-id="element-3461" data-mp="6hxszmmfjhx8">
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-3462" data-mp="1ha3549xt74l">
          {FORMATS.map(item => <li key={item.key} data-id="element-3463" data-mp="20tmanwk1pylb">
              <label className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-150 ease-out ${format === item.key ? 'border-brand-700 bg-brand-50' : 'border-line-strong hover:border-ink-subtle'}`} data-id="element-3464" data-mp="20std9m6z8lfu">
                <input type="radio" name="export-format" checked={format === item.key} onChange={() => setFormat(item.key)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-3465" data-mp="2d43u8o1phmol" />
                <span className="min-w-0" data-id="element-3466" data-mp="1lf0rxdtdqmel">
                  <span className="block text-[13px] font-semibold text-ink" data-id="element-3467" data-mp="1pdrdqoseahme">{item.label}</span>
                  <span className="mt-0.5 block text-[12px] leading-5 text-ink-muted" data-id="element-3468" data-mp="1ud2uhxsrhn3z">
                    Preserves {item.preserves.toLowerCase()}
                  </span>
                </span>
              </label>
            </li>)}
        </ul>

        {textPreview ? <div data-id="element-3469" data-mp="3arm2lxrggms2">
            <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-3470" data-mp="3ar2v7azflgsa">
              Preview
            </p>
            <pre className="max-h-44 overflow-auto rounded-lg border border-line bg-canvas px-3.5 py-3 text-[12px] leading-5 text-ink" data-id="element-3471" data-mp="njonrxanks8a">
              {textPreview}
            </pre>
          </div> : <p className="rounded-lg border border-line bg-canvas px-3.5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-3472" data-mp="wi0bnrk4xbx1">
            {format === 'cznote' ? 'Includes every block, tag, folder, academic link, reminder, attachment record and share role — enough to restore this note exactly.' : format === 'image' ? 'Rendered at 2× for a sharp picture of the note as written.' : `${note.blocks.length} blocks, ${note.attachments.length} attachment${note.attachments.length === 1 ? '' : 's'}${embeddable > 0 ? `, ${embeddable} image${embeddable === 1 ? '' : 's'} embedded directly` : ''}. Files stored remotely are listed by name rather than embedded.`}
          </p>}

        {error && <p className="rounded-lg border border-danger-100 bg-danger-50 px-3.5 py-3 text-[12px] leading-5 text-danger-700" data-id="element-3473" data-mp="28t90fo7u25wj">
            {error}
          </p>}
      </div>
    </Modal>;
}